import { MeasureState } from './measure.state';
import { createReducer, on } from '@ngrx/store';
import { MeasureActions } from './measure.actions';
import { makeItemSelectedActions } from './actions/make-item-selected.actions';
import { getQuestionById } from './util/get-question-by-id';

export const initialState: MeasureState = {
  currentSurveyForEdit: null,
  isSavingSurveyMetaData: false,
  questionTypes: [],
  surveyVersionTree: {
    idobject: null,
    name: '',
    display: '',
    creationDate: '',
    active: true,
    blocks: [],
    embeddedFields: [],
    flowItems: [],
  },
  surveyFlowTree: [],
  logicTypes: [],
  expressionTypes: [],
  flowOperators: [],
  flowItemTypes: [],
  flowQuestionsList: [],
  embeddedFields: [],
  flowConjunctions: [],
  selectedBlock: null,
  selectedQuestion: null,
  selectedChoice: null,
  expandedBlocks: {},
  targetTypes: [],
};

export const measureReducer = createReducer(
  initialState,

  // reset store to initial value; static reference data is not measure-specific, so keep it to avoid
  // reloading it on every wizard tab switch
  on(MeasureActions.resetStore, (state) => ({
    ...initialState,
    questionTypes: state.questionTypes,
    logicTypes: state.logicTypes,
    expressionTypes: state.expressionTypes,
    flowOperators: state.flowOperators,
    flowItemTypes: state.flowItemTypes,
    flowConjunctions: state.flowConjunctions,
    targetTypes: state.targetTypes,
  })),

  // load survey version tree received from BE into store. Every mutation ends in this reload, so re-point the
  // selection at the freshly loaded question: the side panel and the Recode Values dialog read `selectedQuestion`
  // directly and would otherwise keep showing the snapshot taken when the question was clicked.
  on(MeasureActions.loadSurveyVersionTree, (state, { surveyVersionTree }) => ({
    ...state,
    surveyVersionTree,
    selectedQuestion: state.selectedQuestion
      ? getQuestionById(surveyVersionTree, state.selectedQuestion.idobject)
      : null,
  })),

  on(makeItemSelectedActions.selectBlock, (state, { block }) => ({
    ...state,
    selectedBlock: block,
  })),

  on(makeItemSelectedActions.selectMeasureQuestion, (state, { question }) => ({
    ...state,
    selectedQuestion: question,
  })),

  on(makeItemSelectedActions.selectChoice, (state, { choice }) => ({
    ...state,
    selectedChoice: choice,
  })),

  // question types
  on(MeasureActions.loadSurveyVersionQuestionTypesSuccess, (state, { questionTypes }) => ({
    ...state,
    questionTypes,
  })),
  on(MeasureActions.loadSurveyVersionQuestionTypesFailure, (state) => ({
    ...state,
    questionTypes: [],
  })),

  on(MeasureActions.setCurrentSurveyForEdit, (state, currentSurveyForEdit) => ({
    ...state,
    currentSurveyForEdit,
  })),

  on(MeasureActions.editSurveyMetaData, (state) => ({
    ...state,
    isSavingSurveyMetaData: true,
  })),

  // keep the edited name/description in the store so the wizard header and the Save button state follow it
  on(MeasureActions.editSurveyMetaDataSuccess, (state, { display, description }) => ({
    ...state,
    isSavingSurveyMetaData: false,
    currentSurveyForEdit: state.currentSurveyForEdit
      ? { ...state.currentSurveyForEdit, surveyDisplayName: display, surveyDescription: description }
      : null,
  })),

  on(MeasureActions.editSurveyMetaDataFailure, (state) => ({
    ...state,
    isSavingSurveyMetaData: false,
  })),

  on(MeasureActions.updateCurrentSurveyForEdit, (state, updatedFields) => ({
    ...state,
    currentSurveyForEdit: {
      ...state.currentSurveyForEdit!,
      ...updatedFields,
    },
  })),

  on(MeasureActions.toggleExpandBlock, (state, block) => ({
    ...state,
    expandedBlocks: {
      ...state.expandedBlocks,
      [block.blockId]: !state.expandedBlocks[block.blockId],
    },
  })),

  /* flow */
  on(MeasureActions.getFlowItemLogicTypesSuccess, (state, { logicTypes }) => ({
    ...state,
    logicTypes,
  })),

  on(MeasureActions.getFlowOperatorsSuccess, (state, { flowOperators }) => ({
    ...state,
    flowOperators,
  })),

  on(MeasureActions.getFlowConjunctionsSuccess, (state, { flowConjunctions }) => ({
    ...state,
    flowConjunctions,
  })),

  on(MeasureActions.getFlowItemTypesSuccess, (state, { flowItemTypes }) => ({
    ...state,
    flowItemTypes,
  })),

  on(MeasureActions.getVersionEmbeddedFieldsSuccess, (state, { embeddedFields }) => ({
    ...state,
    embeddedFields,
  })),

  on(MeasureActions.getAllSurveyVersionQuestionsSuccess, (state, { flowQuestionsList }) => ({
    ...state,
    flowQuestionsList,
  })),

  on(MeasureActions.getAllFlowItemsSuccess, (state, { flowItems }) => ({
    ...state,
    surveyFlowTree: flowItems,
  })),

  on(MeasureActions.loadFlowTargetTypesSuccess, (state, { targetTypes }) => ({
    ...state,
    targetTypes,
  })),

  on(MeasureActions.getFlowItemExpressionTypesSuccess, (state, { expressionTypes }) => ({
    ...state,
    expressionTypes,
  })),
  /* flow - end */
);
